import { useEffect, useRef, useState, useCallback } from 'react';
import Select2, { components } from 'react-select';
import { Api } from '../../../services';
import './ReportTypeInput.scss';

const MenuList = (props) => {
  const { children } = props;
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        props.selectProps?.onLoadMore?.();
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [props.selectProps]);

  return (
    <components.MenuList {...props} innerRef={elRef}>
      {children}
    </components.MenuList>
  );
};

const MapSelectInput = ({ onMapChange }) => {
  const didInitRef = useRef(false);

  const [selectedMap, setSelectedMap] = useState(null);
  const [mapOptions, setMapOptions] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const unwrap = (res) => res?.data ?? res ?? {};

  const toRelative = (next) => {
    if (!next) return null;
    return next.replace(/^\/?api\/?/i, '').replace(/^\//, '');
  };

  const buildOptions = (maps) =>
    (maps ?? [])
      .map((m) => ({ value: m.mapId, label: m.name }))
      .filter((o) => o.value != null && o.label);

  const fetchPage = useCallback(
    async (url, { append } = { append: true }) => {
      if (!url || isLoading) return;

      setIsLoading(true);
      try {
        const res = await Api.get(url);
        const payload = unwrap(res);

        const pageItems = payload.value ?? payload.items ?? [];
        const newOptions = buildOptions(pageItems);

        setMapOptions((prev) => {
          const merged = append ? [...prev, ...newOptions] : newOptions;

          const seen = new Set();
          return merged.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
        });

        setNextUrl(payload.next ? toRelative(payload.next) : null);
      } catch (e) {
        console.error('Error fetching maps:', e);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading],
  );

  // PRELOAD page 1 so first open always has a scrollable list
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    fetchPage('v2/maps', { append: false });
  }, [fetchPage]);

  const onLoadMore = () => {
    if (nextUrl) fetchPage(nextUrl, { append: true });
  };

  const handleChange = (opt) => {
    setSelectedMap(opt);
    onMapChange?.(opt?.value, opt?.label);
  };

  return (
    <Select2
      options={mapOptions}
      value={selectedMap}
      placeholder="Select a Map"
      onChange={handleChange}
      isLoading={isLoading}
      onLoadMore={onLoadMore}
      components={{ MenuList }}
      maxMenuHeight={260}
      formatOptionLabel={(option, { context }) => {
        if (context === 'menu') {
          return (
            <div>
              <div>{option.label}</div>
              <div id="map-id-value">Map ID: {option.value}</div>
            </div>
          );
        }
        // when closed, just show the label (map name)
        return option.label;
      }}
      styles={{
        menu: (provided) => ({ ...provided, zIndex: 9999 }),
        control: (base) => ({
          ...base,
          border: '1px solid lightgray',
          boxShadow: 'none',
          '&:hover': { border: '1px solid lightgray' },
          '&:focus-within': {
            borderColor: 'var(--c-primary)',
            boxShadow: '0 0 0 1px var(--c-primary)',
          },
        }),
        option: (styles, state) => ({
          ...styles,
          backgroundColor: state.isSelected ? 'var(--c-primary)' : null,
          color: state.isSelected ? 'white' : 'black',
          '&:hover': {
            backgroundColor: state.isSelected ? 'var(--c-primary)' : '#e0ecfc',
          },
        }),
      }}
    />
  );
};

export default MapSelectInput;
